import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { StandardButton, FIELD_CLASS } from '../../../../../component/common/StandardUI';

const AddDocumentFolderModal = ({ open, onClose, onSubmit, submitting }) => {
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    onSubmit(folderName.trim());
  };

  const handleClose = () => {
    setFolderName('');
    setError('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') handleClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      <div
        className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-input shadow-xl w-full max-w-md mx-4 p-6"
        onKeyDown={handleKeyDown}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">New Folder</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="folderName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Folder Name <span className="text-red-500">*</span>
              <span className="float-right text-xs text-gray-400">{folderName.length}/50</span>
            </label>
            <input
              ref={inputRef}
              type="text"
              id="folderName"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value);
                setError('');
              }}
              placeholder="e.g. Annual Wellbeing Strategy"
              className={FIELD_CLASS}
              maxLength={50}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {!error && folderName.length >= 50 && (
              <p className="text-xs text-amber-500 mt-1">Folder name is capped at 50 characters.</p>
            )}
          </div>

          <div className="flex gap-3">
            <StandardButton
              text="Cancel"
              variant="secondary"
              onClick={handleClose}
              className="flex-1"
            />
            <StandardButton
              text={submitting ? 'Creating...' : 'Create Folder'}
              type="submit"
              disabled={!folderName.trim() || submitting}
              className="flex-1"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

AddDocumentFolderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool
};

export default AddDocumentFolderModal;
